import React from 'react'
import {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  useController,
} from 'react-hook-form'
import {getFieldError} from './getFieldError'

type WheelInputProps<T extends FieldValues> = {
  name: Path<T>
  label?: string
  subLabel?: string
  placeholder?: string
  helperText?: string
  control: Control<T>
  errors?: FieldErrors<T>
  required?: boolean | string
  className?: string
  inputClassName?: string
  prefix?: string
  suffix?: string
  layout?: 'stacked' | 'half'
  min: number
  max: number
  step?: number
  defaultValue?: number

  options?: Omit<RegisterOptions<T, Path<T>>, 'setValueAs' | 'valueAsNumber' | 'valueAsDate'>
  formatValue?: (value: number) => string
  onChange?: (value: number) => void
}

const WHEEL_ITEM_HEIGHT = 38
const WHEEL_VISIBLE_ROWS = 5

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const toFiniteNumber = (value: number, fallback: number) => {
  return Number.isFinite(value) ? value : fallback
}

const buildWheelOptions = (min: number, max: number, step: number) => {
  const safeMin = toFiniteNumber(min, 0)
  const safeMax = toFiniteNumber(max, safeMin)
  const start = Math.min(safeMin, safeMax)
  const end = Math.max(safeMin, safeMax)
  const interval = step > 0 && Number.isFinite(step) ? step : 1
  const count = Math.floor((end - start) / interval) + 1
  const options = Array.from({length: Math.max(count, 1)}, (_, index) =>
    Number((start + index * interval).toFixed(6))
  ).filter((value) => value <= end)
  const lastValue = options[options.length - 1]

  if (lastValue !== end) {
    options.push(end)
  }

  return {
    min: start,
    max: end,
    step: interval,
    options,
  }
}

const normalizeWheelValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return null
  }

  return parsed
}

const getNearestOptionIndex = (options: number[], value: number) => {
  if (options.length === 0) {
    return 0
  }

  let nearestIndex = 0
  let nearestDistance = Math.abs(options[0] - value)

  for (let index = 1; index < options.length; index += 1) {
    const distance = Math.abs(options[index] - value)

    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestIndex = index
    }
  }

  return nearestIndex
}

const normalizeWheelValueInRange = (value: unknown, options: number[]) => {
  const normalized = normalizeWheelValue(value)

  if (normalized === null || options.length === 0) {
    return null
  }

  return options[getNearestOptionIndex(options, normalized)]
}

const scrollToWheelValue = (
  container: HTMLDivElement | null,
  options: number[],
  value: number | null,
  behavior: ScrollBehavior
) => {
  if (!container || options.length === 0) {
    return
  }

  const fallbackValue = options[0]
  const index = getNearestOptionIndex(options, value ?? fallbackValue)

  container.scrollTo({
    top: index * WHEEL_ITEM_HEIGHT,
    behavior,
  })
}

export const WheelInput = <T extends FieldValues>({
  name,
  label,
  subLabel = '',
  placeholder = 'Select value',
  helperText = '',
  control,
  errors,
  required = false,
  className = '',
  inputClassName = '',
  prefix = '',
  suffix = '',
  layout = 'stacked',
  min,
  max,
  step = 1,
  defaultValue,
  options = {},

  formatValue,
  onChange,
}: WheelInputProps<T>) => {
  const range = React.useMemo(() => buildWheelOptions(min, max, step), [max, min, step])
  const defaultWheelValue = React.useMemo(
    () => normalizeWheelValueInRange(defaultValue, range.options),
    [defaultValue, range.options]
  )
  const formatDisplayValue = React.useCallback(
    (value: number) =>
      formatValue?.(value) ?? `${prefix}${value.toLocaleString('en-AU')}${suffix}`,
    [formatValue, prefix, suffix]
  )
  const {field} = useController({
    name,
    control,
    ...(defaultWheelValue !== null ? {defaultValue: defaultWheelValue as T[Path<T>]} : {}),
    rules: {
      ...options,
      required,
      min: options.min ?? {
        value: range.min,
        message: `Minimum is ${formatDisplayValue(range.min)}.`,
      },
      max: options.max ?? {
        value: range.max,
        message: `Maximum is ${formatDisplayValue(range.max)}.`,
      },
    },
  })
  const error = getFieldError(errors, name)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = React.useRef<number | null>(null)
  const selectedValue = normalizeWheelValueInRange(field.value, range.options)
  const wrapperClassName = layout === 'half' ? 'col-12 col-md-6 mb-0' : 'mb-5'
  const wheelHeight = WHEEL_ITEM_HEIGHT * WHEEL_VISIBLE_ROWS
  const summaryText = selectedValue === null ? placeholder : formatDisplayValue(selectedValue)

  React.useEffect(() => {
    scrollToWheelValue(scrollRef.current, range.options, selectedValue, 'auto')
  }, [range.options, selectedValue])

  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    const rawValue = normalizeWheelValue(field.value)

    if (rawValue === null || selectedValue === null || rawValue === selectedValue) {
      return
    }

    field.onChange(selectedValue)
  }, [field.onChange, field.value, selectedValue])

  const handleSelect = (nextValue: number) => {
    field.onChange(nextValue)
    onChange?.(nextValue)
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (scrollTimeoutRef.current !== null) {
      window.clearTimeout(scrollTimeoutRef.current)
    }

    const container = event.currentTarget
    scrollTimeoutRef.current = window.setTimeout(() => {
      const index = clamp(
        Math.round(container.scrollTop / WHEEL_ITEM_HEIGHT),
        0,
        range.options.length - 1
      )
      const nextValue = range.options[index]

      handleSelect(nextValue)
      scrollToWheelValue(container, range.options, nextValue, 'smooth')
    }, 90)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex =
      selectedValue === null ? 0 : getNearestOptionIndex(range.options, selectedValue)
    const selectByIndex = (index: number) => {
      const safeIndex = clamp(index, 0, range.options.length - 1)
      const nextValue = range.options[safeIndex]

      handleSelect(nextValue)
      scrollToWheelValue(scrollRef.current, range.options, nextValue, 'smooth')
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      selectByIndex(currentIndex + 1)
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      selectByIndex(currentIndex - 1)
    }

    if (event.key === 'Home') {
      event.preventDefault()
      selectByIndex(0)
    }

    if (event.key === 'End') {
      event.preventDefault()
      selectByIndex(range.options.length - 1)
    }
  }

  return (
    <div className={`${wrapperClassName} wizard-wheel-input ${className}`.trim()}>
      {label && (
        <label className='form-label fw-semibold' htmlFor={String(name)}>
          {label} {required && <span className='text-danger'>*</span>}
        </label>
      )}

      <div
        className={`wizard-wheel-input-picker ${inputClassName}`.trim()}
        style={{height: wheelHeight}}
      >
        <div className='wizard-wheel-input-sublabel'>{subLabel}</div>
        <div className={`wizard-budget-wheel ${error ? 'is-invalid' : ''}`.trim()}>
          <div className='wizard-budget-wheel-body'>
            <div className='wizard-budget-wheel-window' aria-hidden='true'></div>

            <div
              id={String(name)}
              ref={scrollRef}
              className='wizard-budget-wheel-scroller'
              onBlur={field.onBlur}
              onKeyDown={handleKeyDown}
              onScroll={handleScroll}
              role='listbox'
              tabIndex={0}
              aria-invalid={Boolean(error)}
              aria-label={label || placeholder}
            >
              {range.options.map((option) => (
                <button
                  key={option}
                  type='button'
                  className={`wizard-budget-wheel-option fs-2 ${
                    selectedValue === option ? 'is-selected' : ''
                  }`.trim()}
                  onClick={() => {
                    handleSelect(option)
                    scrollToWheelValue(scrollRef.current, range.options, option, 'smooth')
                  }}
                  role='option'
                  aria-selected={selectedValue === option}
                >
                  {formatDisplayValue(option)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {helperText && <small className='text-muted d-block mt-1 fs-6'>{helperText}</small>}

      {error && <div className='text-danger mt-1'>{error.message}</div>}
    </div>
  )
}
